import { observer } from "mobx-react";
import React from "react";
import { EditInput } from "@fidget/common/components/form/editInput";
import { InputGroup } from "@fidget/common/components/form/inputGroup";
import { IUserProfile } from "../../../models/currentUser/UserProfile";
import { EditMeLabel } from "../../editMeLabel/editMeLabel";
import { DiscardApplyButtons } from "../discardApplyButtons";
import styles from "./agentNameSettings.css";
import { AppPermission } from "@closerplatform/spinner-openapi";

export interface Props {
  userProfile: IUserProfile;
}

export interface State {
  editName: boolean;
  areNamesValid: boolean;
  firstName?: string;
  lastName?: string;
}

/**
 * Component to change user first or last name, with display and edit mode
 * XXX: could be used to change ANY profile (hence the userProfile prop)
 *   but backend allows for updating current user only
 * TODO: refactor to Formstate to unify validation / input handling
 */
@observer
export class AgentNameSettings extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      editName: false,
      areNamesValid: true
    };
  }

  canEdit = this.props.userProfile.doesHavePermission(AppPermission.UpdatePersonalData);

  componentWillReceiveProps(newProps: Props) {
    if (newProps.userProfile !== this.props.userProfile) {
      const { firstName, lastName } = newProps.userProfile;
      this.setState({ firstName, lastName, editName: false });
    }
  }

  toggleNameEditing = () => {
    const { firstName, lastName } = this.props.userProfile;
    // on each mode toggle, reset values to originals
    this.setState({ firstName, lastName, editName: !this.state.editName });
  };

  areNamesValid = () => {
    const { firstName, lastName } = this.state;
    this.setState({ areNamesValid: this.isValid(firstName) && this.isValid(lastName) });
  };

  isValid = (name: string | undefined) => {
    return !!(name && name.length > 1 && name.length < 65);
  };

  handleFirstNameChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ firstName: ev.target.value }, this.areNamesValid);
  };

  handleLastNameChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ lastName: ev.target.value }, this.areNamesValid);
  };

  /**
   * Handle save request
   * TODO: add feedback in toast after successfully changing name?
   */
  saveAgentName = () => {
    const { firstName, lastName } = this.state;
    this.props.userProfile
      .update({ firstName, lastName }, true)
      .then(() => this.setState({ editName: false }));
  };

  renderEdit = () => {
    const { userProfile } = this.props;
    const { firstName, lastName, areNamesValid } = this.state;
    return (
      <form onSubmit={(ev) => ev.preventDefault()}>
        <InputGroup>
          <EditInput
            className={styles["agent-name-input"]}
            placeholder={userProfile.firstName}
            value={firstName || ""}
            type={"text"}
            onChange={this.handleFirstNameChange}
            autoFocus
          />
        </InputGroup>
        <InputGroup>
          <EditInput
            className={styles["agent-name-input"]}
            placeholder={userProfile.lastName}
            value={lastName || ""}
            type={"text"}
            onChange={this.handleLastNameChange}
          />
        </InputGroup>
        <DiscardApplyButtons
          onApply={this.saveAgentName}
          onDiscard={this.toggleNameEditing}
          applyDisabled={!areNamesValid}
          alignToLeft
        />
      </form>
    );
  };

  renderName = () => {
    const { fullName } = this.props.userProfile;
    return this.canEdit ? (
      <EditMeLabel
        value={fullName || ""}
        onClick={this.toggleNameEditing}
        className={styles["name-label"]}
      />
    ) : (
      <div className={styles["name-label"]}>{fullName}</div>
    );
  };

  render() {
    const { userProfile } = this.props;
    return (
      <div className={styles["agent-info"]}>
        {this.state.editName ? (
          this.renderEdit()
        ) : (
          <>
            {this.renderName()}
            <div className={styles["agent-email"]}>{userProfile.email || ""}</div>
          </>
        )}
      </div>
    );
  }
}
